import { Box } from '@mui/material';
import { Control, useFormState, useWatch } from 'react-hook-form';

import { TextInput } from '@/components/Form/TextInput';

import { useTranslate } from '@/hooks/useTranslate';

import {
  CharactersLeft,
  CurrencyLabel,
  FieldLabel,
  FinanceBlock,
} from '../common';
import { LiabilityFormValues } from './type';

const MAX_TYPE_LENGTH = 30;
const MAX_VALUE_DIGITS = 12;

type LiabilityBlockProps = {
  index: number;
  total: number;
  currencyDisplay: string;
  control: Control<LiabilityFormValues>;
  onDelete: () => void;
};

export function LiabilityBlock({
  index,
  total,
  currencyDisplay,
  control,
  onDelete,
}: Readonly<LiabilityBlockProps>) {
  const { t } = useTranslate();

  const { errors } = useFormState({
    control,
    name: [`liabilities.${index}.liabilityType`, `liabilities.${index}.amount`],
  });
  const typeValue =
    useWatch({ control, name: `liabilities.${index}.liabilityType` }) ?? '';

  return (
    <FinanceBlock
      title={t('moreInfo.form.yourFinances.liabilities.liabilityBlockTitle')}
      counter={`${index + 1}/${total}`}
      isDeletable={total > 1}
      onDelete={onDelete}
    >
      <Box mb={2}>
        <FieldLabel>
          {t('moreInfo.form.yourFinances.liabilities.liabilityTypeLabel')}
        </FieldLabel>
        <TextInput<LiabilityFormValues>
          control={control}
          name={`liabilities.${index}.liabilityType`}
          error={!!errors.liabilities?.[index]?.liabilityType}
          helperText={errors.liabilities?.[index]?.liabilityType?.message}
          placeholder={t(
            'moreInfo.form.yourFinances.liabilities.liabilityTypePlaceholder',
          )}
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_TYPE_LENGTH }}
        />
        <CharactersLeft count={MAX_TYPE_LENGTH - typeValue.length} />
      </Box>

      <Box>
        <FieldLabel>
          {t('moreInfo.form.yourFinances.liabilities.valueLabel')}
        </FieldLabel>
        <TextInput<LiabilityFormValues>
          control={control}
          name={`liabilities.${index}.amount`}
          error={!!errors.liabilities?.[index]?.amount}
          helperText={errors.liabilities?.[index]?.amount?.message}
          placeholder={t(
            'moreInfo.form.yourFinances.liabilities.valuePlaceholder',
          )}
          inputMode="numeric"
          fullWidth
          size="small"
          template="currency"
          startAdornment={<CurrencyLabel>{currencyDisplay}</CurrencyLabel>}
          inputProps={{ maxLength: MAX_VALUE_DIGITS }}
        />
      </Box>
    </FinanceBlock>
  );
}
